import { audioSettingsStorage } from "@/utils/audioSettings";

export interface MicCaptureOptions {
  /** Receives ~20ms PCM16LE @24kHz chunks ready for the transcription WebSocket. */
  onChunk: (chunk: ArrayBuffer) => void;
}

/**
 * Captures the microphone as 24kHz PCM16 for the live transcription bridge.
 *
 * Runs its own AudioContext at 24kHz so the browser does the resampling; a
 * worklet converts to PCM16 and emits fixed 20ms chunks. Exposes an
 * AnalyserNode for the input level bars (read via rAF, never through React
 * state). Muting swallows chunks locally without stopping the hardware, so
 * unmute is instant and the mic indicator stays truthful.
 */
export class MicCapture {
  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private sourceNode: MediaStreamAudioSourceNode | null = null;
  private muted = false;

  analyser: AnalyserNode | null = null;

  constructor(private options: MicCaptureOptions) {}

  async start(): Promise<void> {
    const selectedDevice = audioSettingsStorage.getSelectedDevice();

    // echoCancellation is the primary defense against the model hearing its
    // own speech from the speakers.
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
        ...(selectedDevice?.deviceId
          ? { deviceId: { exact: selectedDevice.deviceId } }
          : {}),
      },
    });

    this.context = new AudioContext({ sampleRate: 24000 });
    await this.context.audioWorklet.addModule("/worklets/pcm-capture.js");

    this.sourceNode = this.context.createMediaStreamSource(this.stream);

    this.analyser = this.context.createAnalyser();
    this.analyser.fftSize = 256;
    this.analyser.smoothingTimeConstant = 0.8;
    this.sourceNode.connect(this.analyser);

    this.workletNode = new AudioWorkletNode(this.context, "pcm-capture", {
      numberOfInputs: 1,
      numberOfOutputs: 0,
      channelCount: 1,
    });
    this.workletNode.port.onmessage = (event: MessageEvent<ArrayBuffer>) => {
      if (!this.muted) {
        this.options.onChunk(event.data);
      }
    };
    this.sourceNode.connect(this.workletNode);

    if (this.context.state === "suspended") {
      await this.context.resume();
    }
  }

  setMuted(muted: boolean) {
    this.muted = muted;
  }

  isMuted() {
    return this.muted;
  }

  async stop(): Promise<void> {
    if (this.workletNode) {
      this.workletNode.port.onmessage = null;
      this.workletNode.disconnect();
      this.workletNode = null;
    }
    if (this.sourceNode) {
      this.sourceNode.disconnect();
      this.sourceNode = null;
    }
    this.analyser = null;
    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop());
      this.stream = null;
    }
    if (this.context) {
      try {
        await this.context.close();
      } catch {
        // Already closed.
      }
      this.context = null;
    }
  }
}

export const micCaptureIsSupported = (): boolean =>
  typeof navigator !== "undefined" &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof AudioContext !== "undefined" &&
  typeof AudioWorkletNode !== "undefined";
